import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  Grid,
  IconButton,
  DialogContent,
  Box,
  CircularProgress,
  Typography,
  Stack,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PrintIcon from '@mui/icons-material/Print';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import Point from '../../../assets/svg/Point';
import { setSelectedIncidentInfo } from '../../../store/incidentsReducer';
import { useDispatch } from 'react-redux';
import SquareExclamatioMark from '../../../assets/svg/SquareExclamatioMark';
import Clock from '../../../assets/svg/Clock';
import moment from 'moment';
import Graphics from '../../../assets/svg/Graphics';
import {
  callStatusColorMatching,
  statusMatching,
} from '../../../pages/Incidents/utils/statusMatching';
import { dateTimeFormat } from '../../../utils/formatDate';
import { getIncidentAddress } from '../../../pages/Incidents/utils/incidentUtils';
import { callStatusMatching } from '../../../pages/Incidents/utils/callStatusMatching';

// NOTE: IncidentPrintView is intentionally NOT imported here.
// It is rendered once at the Incidents page level (Incidents.jsx) via a React
// Portal to <body>, so it works for both row-level and modal printing.
// Rendering it here too caused the report to appear twice on the same print.

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    background: '#2121214c',
  },
  '& .MuiPaper-root': {
    maxWidth: 832,
    width: 832,
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(3),
    borderTop: 'none',
    '& p.sectionTitle': {
      fontSize: '18px',
      fontWeight: 500,
      lineHeight: '20px',
      color: '#6B6B6B',
      marginBottom: '10px',
    },
    '& p.subTitle': {
      fontSize: '16px',
      fontWeight: 500,
      color: '#6B6B6B',
    },
    '& p.text': {
      fontSize: '16px',
      fontWeight: 500,
      color: '#000000df',
      marginTop: '10px',
      whiteSpace: 'wrap',
      wordBreak: 'break-all',
    },
    '& .unit-box': {
      minHeight: 145,
      background: '#F3F3F3B2',
      borderRadius: '4px',
      padding: '15px',
    },
    '& .unit-title': {
      fontSize: '14px',
      fontWeight: 600,
      color: 'rgba(0, 0, 0, 0.87)',
      marginBottom: '15px',
    },
    '& .unit-subtitle': {
      fontSize: '14px',
      fontWeight: 600,
      color: 'rgba(0, 0, 0, 0.57)',
      marginTop: '10px',
      marginBottom: '10px',
    },
    '& .unit-text': {
      fontSize: '14px',
      fontWeight: 400,
      color: 'rgba(0, 0, 0, 0.87)',
      marginTop: '8px',
      '& span': {
        fontWeight: 600,
      },
    },
  },
}));

export default function ViewIncidentModal({ viewIncident, setViewIncident }) {
  const { selectedIncidentInfo, incidentInfoFetching, callStatus } =
    useSelector((state) => state.incidentsReducer);
  const dispatch = useDispatch();

  const closeHandler = () => {
    setViewIncident(false);
  };
  const onTransitionExitedHandler = () => {
    dispatch(setSelectedIncidentInfo(null));
  };

  const nerisStatus = useMemo(
    () => statusMatching(selectedIncidentInfo?.neris_status),
    [selectedIncidentInfo]
  );

  const handlePrint = () => {
    window.print();
  };

  return (
    <BootstrapDialog
      onClose={closeHandler}
      aria-labelledby="view-incident-modal"
      open={!!viewIncident}
      closeAfterTransition
      onTransitionExited={onTransitionExitedHandler}
    >
      <DialogTitle sx={{ m: 0, p: 3, pb: 0 }} id="customized-dialog-title">
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Box
            sx={{
              px: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              background: '#F3F3F3',
              borderRadius: '12px',
              width: 'fit-content',
              height: 57,
            }}
          >
            <Typography
              sx={{
                fontSize: '22px',
                fontWeight: 500,
                lineHeight: '25px',
                mr: 2,
              }}
            >
              Incident
            </Typography>
            <Typography
              sx={{ fontSize: '22px', fontWeight: 300, lineHeight: '25px' }}
            >
              {selectedIncidentInfo?.dispatch.incident_number}
            </Typography>
          </Box>

          {/* Print button — triggers window.print(); the portal in Incidents.jsx handles the output */}
          {selectedIncidentInfo && !incidentInfoFetching && (
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrint}
              size="small"
              sx={{
                borderColor: '#212121',
                color: '#212121',
                mr: 5,
                '&:hover': { borderColor: '#212121', background: '#f3f3f3' },
              }}
            >
              Print
            </Button>
          )}
        </Stack>
      </DialogTitle>

      <IconButton
        aria-label="close"
        onClick={closeHandler}
        sx={() => ({
          position: 'absolute',
          right: 8,
          top: 8,
          color: '#212121',
        })}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent dividers>
        {incidentInfoFetching ? (
          <Box
            sx={{ minHeight: '100px' }}
            className="w-100 h-100 d-flex justify-content-center align-items-center"
          >
            <CircularProgress />
          </Box>
        ) : (
          !!selectedIncidentInfo && (
            <>
              <Box sx={{ borderBottom: '1px solid #E1E1E1', pb: 2 }}>
                <Typography className="sectionTitle">
                  Incident Identification
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  useFlexGap
                  sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Box sx={{ width: '50%' }}>
                    <Box className="d-flex align-items-center">
                      <Point />
                      <Typography className="subTitle ms-1">Address</Typography>
                    </Box>
                    <Typography className="text">
                      {getIncidentAddress(selectedIncidentInfo.base.location)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '50%' }}>
                    <Box className="d-flex align-items-center">
                      <SquareExclamatioMark />
                      <Typography className="subTitle ms-1">Type</Typography>
                    </Box>
                    {selectedIncidentInfo.incident_types?.length ? (
                      selectedIncidentInfo.incident_types.map(
                        (item, index, arr) => (
                          <Typography
                            className="text mt-1"
                            key={item.type + index}
                          >
                            {item.type}
                            {arr.length - 1 > index ? ', ' : ''}
                          </Typography>
                        )
                      )
                    ) : selectedIncidentInfo.base?.cad_type ||
                      selectedIncidentInfo.cad_type ? (
                      <Typography className="text mt-1">
                        {selectedIncidentInfo.base?.cad_type ||
                          selectedIncidentInfo.cad_type}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ borderBottom: '1px solid #E1E1E1', pb: 2, mt: 2 }}>
                <Typography className="sectionTitle">
                  Call Information
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  useFlexGap
                  sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Box sx={{ width: '25%' }}>
                    <Box className="d-flex align-items-center">
                      <Clock />
                      <Typography className="subTitle ms-1">Create</Typography>
                    </Box>
                    <Typography className="text">
                      {dateTimeFormat(selectedIncidentInfo.dispatch.call_create)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '25%' }}>
                    <Box className="d-flex align-items-center">
                      <Clock />
                      <Typography className="subTitle ms-1">Answered</Typography>
                    </Box>
                    <Typography className="text">
                      {dateTimeFormat(selectedIncidentInfo.dispatch.call_answered)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '25%' }}>
                    <Box className="d-flex align-items-center">
                      <Clock />
                      <Typography className="subTitle ms-1">Arrival</Typography>
                    </Box>
                    <Typography className="text">
                      {dateTimeFormat(selectedIncidentInfo.dispatch.call_arrival)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '25%' }}>
                    <Box className="d-flex align-items-center">
                      <Graphics />
                      <Typography className="subTitle ms-1">Status</Typography>
                    </Box>
                    <Box
                      sx={{
                        background: callStatusColorMatching(
                          selectedIncidentInfo.status?.toLowerCase()
                        )?.bg,
                        padding: '5px 15px',
                        borderRadius: '3px',
                        width: 'fit-content',
                        mt: '10px',
                      }}
                    >
                      {callStatusMatching(callStatus, selectedIncidentInfo.status)}
                    </Box>
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ pb: 2, mt: 2 }}>
                <Typography className="sectionTitle">
                  Status Information
                </Typography>
                <Stack
                  direction="row"
                  spacing={2}
                  useFlexGap
                  sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <Box sx={{ width: '50%' }}>
                    <Box className="d-flex align-items-center">
                      <Graphics />
                      <Typography className="subTitle ms-1">
                        NERIS Status
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        background: nerisStatus.bg,
                        color: nerisStatus.color,
                        padding: '5px 15px',
                        borderRadius: '999px',
                        width: 'fit-content',
                        mt: '10px',
                      }}
                    >
                      {nerisStatus.text}
                    </Box>
                  </Box>
                </Stack>
              </Box>

              <Box sx={{ borderBottom: '1px solid #E1E1E1', pb: 2, mt: 3 }}>
                <Typography className="sectionTitle mb-0">
                  Assigned Apparatus
                </Typography>
                <Grid sx={{ flexGrow: 1, mt: '0' }} container spacing={2}>
                  {selectedIncidentInfo.unit_responses?.map((unit, index) => (
                    <Grid item xs={4} key={unit.reported_unit_id + index}>
                      <Box key={index} className="unit-box">
                        <Typography className="unit-title">
                          Unit ID: {unit.reported_unit_id}
                        </Typography>
                        {unit.dispatch ? (
                          <Typography className="unit-text">
                            Dispatch:{' '}
                            {moment(unit.dispatch).format('DD.MM.YYYY')}{', '}
                            <span>{moment(unit.dispatch).format('HH:mm:ss')}</span>
                          </Typography>
                        ) : (
                          <Typography className="unit-text">Dispatch: -</Typography>
                        )}
                        {unit.on_scene ? (
                          <Typography className="unit-text">
                            On Scene:{' '}
                            {moment(unit.on_scene).format('DD.MM.YYYY')}{', '}
                            <span>{moment(unit.on_scene).format('HH:mm:ss')}</span>
                          </Typography>
                        ) : (
                          <Typography className="unit-text">On Scene: -</Typography>
                        )}
                        {unit.unit_clear ? (
                          <Typography className="unit-text">
                            Clear:{' '}
                            {moment(unit.unit_clear).format('DD.MM.YYYY')}{', '}
                            <span>{moment(unit.unit_clear).format('HH:mm:ss')}</span>
                          </Typography>
                        ) : (
                          <Typography className="unit-text">Clear: -</Typography>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>

              {selectedIncidentInfo.note && (
                <Box sx={{ pb: 2, mt: 3 }}>
                  <Typography className="sectionTitle mb-0">Notes</Typography>
                  <Typography className="text">
                    {selectedIncidentInfo.note}
                  </Typography>
                </Box>
              )}
            </>
          )
        )}
      </DialogContent>
    </BootstrapDialog>
  );
}
